import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [mainLogo, setMainLogo] = useState(null);
  
  // 2FA States
  const [needs2FA, setNeeds2FA] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  const videoRef = useRef(null);
  const codeInputRef = useRef(null);
  const { login, loginWith2FA } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Try to play video on mount
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
    
    // Fetch main logo from settings (public endpoint)
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
        if (response.data.main_logo) {
          setMainLogo(response.data.main_logo);
        }
      } catch (error) {
        console.log('Using default logo');
      }
    };
    fetchLogo();
  }, []);

  // Focus on code input when 2FA modal opens
  useEffect(() => {
    if (needs2FA && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [needs2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      
      // Check if 2FA is needed
      if (result.needs_verification) {
        setNeeds2FA(true);
        setVerificationId(result.verification_id);
        setEmailHint(result.email_hint || email);
        toast.info('Doğrulama kodu e-postanıza gönderildi');
      } else {
        toast.success('Hoş geldiniz!');
        navigate('/');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Geçersiz kimlik bilgileri';
      toast.error(errorMessage);
      
      // If account is blocked
      if (error.response?.status === 403) {
        setNeeds2FA(false);
        setVerificationCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('Lütfen 6 haneli kodu girin');
      return;
    }
    
    setVerifying(true);
    try {
      await loginWith2FA(email, verificationCode, verificationId);
      toast.success('Doğrulama başarılı! Hoş geldiniz!');
      navigate('/');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Doğrulama başarısız';
      toast.error(errorMessage);
      
      // If account got blocked, reset to login
      if (error.response?.status === 403) {
        setNeeds2FA(false);
        setVerificationCode('');
        setEmail('');
        setPassword('');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const handleBack = () => {
    setNeeds2FA(false);
    setVerificationCode('');
    setVerificationId('');
  };

  return (
    <div className="login-container">
      {/* Video Background */}
      <video 
        ref={videoRef}
        autoPlay 
        loop 
        muted 
        playsInline
        preload="auto"
        className="login-video-bg"
        onCanPlay={() => setVideoLoaded(true)}
        style={{ opacity: videoLoaded ? 1 : 0 }}
      >
        <source 
          src="/login-bg.mp4" 
          type="video/mp4" 
        />
      </video>
      
      {/* Overlay for better readability */}
      <div className="login-overlay"></div>
      
      {/* Login Form - Centered */}
      <div className="login-form-wrapper">
        <div className="login-form-container">
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            marginBottom: '1.5rem' 
          }}>
            <img 
              src={mainLogo || "/rota-logo-new.png"} 
              alt="Rota Maritime Agency" 
              style={{ 
                height: '100px', 
                width: 'auto',
                objectFit: 'contain',
                mixBlendMode: mainLogo ? 'normal' : 'multiply'
              }} 
            />
          </div>
          
          {!needs2FA ? (
            // Normal Login Form
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">E-posta Adresi</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="E-posta adresinizi girin"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Şifre</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="Şifrenizi girin"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
                data-testid="login-btn"
                style={{ width: '100%' }}
              >
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </button>
            </form>
          ) : (
            // 2FA Verification Form
            <form onSubmit={handleVerifyCode}>
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '1.5rem',
                padding: '1rem',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--primary)' }}>
                  Güvenlik Doğrulaması
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
                  Yeni bir cihazdan giriş yapıyorsunuz.<br/>
                  <strong>{emailHint}</strong> adresine gönderilen 6 haneli kodu girin.
                </p>
              </div>
              
              <div className="form-group">
                <label className="form-label" style={{ textAlign: 'center', display: 'block' }}>
                  Doğrulama Kodu
                </label>
                <input
                  ref={codeInputRef}
                  type="text"
                  className="form-input"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  maxLength={6}
                  required
                  data-testid="verification-code-input"
                  style={{ 
                    textAlign: 'center', 
                    fontSize: '1.75rem', 
                    letterSpacing: '0.5rem',
                    fontWeight: 'bold'
                  }}
                />
                <p style={{ 
                  textAlign: 'center', 
                  fontSize: '0.75rem', 
                  color: '#64748b',
                  marginTop: '0.5rem'
                }}>
                  ⏱️ Kod 5 dakika geçerlidir • 3 deneme hakkınız var
                </p>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={verifying || verificationCode.length !== 6}
                data-testid="verify-btn"
                style={{ width: '100%', marginBottom: '0.75rem' }}
              >
                {verifying ? 'Doğrulanıyor...' : 'Doğrula ve Giriş Yap'}
              </button>
              
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleBack}
                style={{ width: '100%' }}
                data-testid="back-btn"
              >
                ← Geri Dön
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
